import useVideoGenerationStore from '../store/videoGenerationStore';
import { toast } from '../hooks/use-toast';

// Use VITE_WS_URL directly if available, otherwise derive from VITE_API_URL
import { WS_BASE_URL } from '../api/config';
const WS_URL = WS_BASE_URL;

class WebSocketManager {
  static instance = null;
  constructor() {
    this.connections = new Map();
    this.reconnectAttempts = new Map();
    this.maxReconnectAttempts = 3;
    this.reconnectDelay = 1000;
  }

  static getInstance() {
    if (!WebSocketManager.instance) {
      WebSocketManager.instance = new WebSocketManager();
    }
    return WebSocketManager.instance;
  }

  // Combined function to handle text segmentation and WebSocket connection
  async startVideoGeneration(prompt, duration, style, userEmail, selectedModel = 'minimax') {
    const { initializeGeneration, clearGeneration, updateGenerationProgress, completeGeneration, setGenerationError } = useVideoGenerationStore.getState();
    let tempGenerationId = crypto.randomUUID();
    let finalGenerationId = null;

    try {
      // Immediately initialize UI with a temporary ID
      initializeGeneration(tempGenerationId, {
        prompt_text: prompt,
        selectedDuration: `${duration}s`,
        selectedStyle: style,
      });

      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      console.log(`[WSM] Starting text segmentation for duration: ${duration}s, style: ${style}`);
      
      const response = await fetch(`${WS_URL.replace('ws', 'http')}/text-segmentor`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ text: prompt, video_length: duration, style: style })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Text segmentation failed');
      }

      const data = await response.json();
      finalGenerationId = data.generation_id;
      console.log(`[WSM] Text segmentation successful, final ID: ${finalGenerationId}`);

      // Swap temp ID with final ID in the store
      clearGeneration(tempGenerationId);
      initializeGeneration(finalGenerationId, {
        prompt_text: prompt,
        selectedDuration: `${duration}s`,
        selectedStyle: style,
      });
      
      // Now, create the WebSocket connection
      this._createConnection(userEmail, finalGenerationId, {
        action: "generate_videos",
        generation_id: finalGenerationId,
        video_length: duration,
        prompt: prompt,
        selected_model: selectedModel,
        aspect_ratio: "16:9"
      });

      return finalGenerationId;

    } catch (error) {
      console.error('[WSM] startVideoGeneration failed:', error);
      setGenerationError(finalGenerationId || tempGenerationId, error.message);
      if (finalGenerationId) clearGeneration(finalGenerationId);
      clearGeneration(tempGenerationId);
      throw error;
    }
  }

  _createConnection(userEmail, generationId, initialMessage) {
    const { updateGenerationProgress, completeGeneration, setGenerationError } = useVideoGenerationStore.getState();
    const token = localStorage.getItem('token');
    const wsUrl = `${WS_URL}/text-to-video/ws/${encodeURIComponent(userEmail)}/${encodeURIComponent(generationId)}?token=${encodeURIComponent(token)}`;
    
    const ws = new WebSocket(wsUrl);
    this.connections.set(generationId, ws);

    ws.onopen = () => {
      console.log(`[WSM] Connected for ${generationId}`);
      if (initialMessage) {
        ws.send(JSON.stringify(initialMessage));
        console.log(`[WSM] Sent initial message to ${generationId}:`, initialMessage);
      }
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      console.log(`[WSM] Received for ${generationId}:`, data);

      switch (data.status) {
        case 'progress':
          updateGenerationProgress(generationId, data.progress || 0, data.message || "Processing...");
          break;
        case 'completed':
          completeGeneration(generationId, data.video_url);
          toast({ title: "Success", description: "Video generated successfully!" });
          this.cleanup(generationId);
          break;
        case 'error':
          setGenerationError(generationId, data.message || "An error occurred");
          toast({ title: "Generation Error", description: data.message, variant: "destructive" });
          this.cleanup(generationId);
          break;
        default:
          break;
      }
    };

    ws.onclose = (event) => {
      console.log(`[WSM] Connection closed for ${generationId}:`, event.code, event.reason);
      this.connections.delete(generationId);
      // Optionally handle unexpected closures and retries here
    };

    ws.onerror = (error) => {
      console.error(`[WSM] Error for ${generationId}:`, error);
      setGenerationError(generationId, "A connection error occurred.");
      this.cleanup(generationId);
    };
  }
  
  cleanup(generationId) {
    const ws = this.connections.get(generationId);
    if (ws) {
      ws.close(1000, "Cleanup");
      this.connections.delete(generationId);
    }
  }

  cleanupAll() {
    console.log('[WSM] Cleaning up all connections');
    for (const [generationId] of this.connections) {
      this.cleanup(generationId);
    }
  }
}

export default WebSocketManager; 